import React from "react";
import { Meta } from "../components/SEO";
import { company } from "../data/company";

const H = ({ children }) => <h2 className="font-display text-2xl text-white">{children}</h2>;

export const PrivacyPage = () => (
  <>
    <Meta
      title="Privacyverklaring | Koning Aannemersbedrijf"
      description="Lees hoe Koning Aannemersbedrijf omgaat met uw persoonsgegevens: doelen, grondslagen, bewaartermijnen, ontvangers en uw rechten onder de AVG."
    />
    <section className="pt-24 pb-20 md:pt-32 md:pb-28" data-testid="privacy-page">
      <div className="max-w-[900px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="eyebrow mb-5">Privacyverklaring</div>
        <h1 className="font-display text-4xl md:text-6xl text-white tracking-tight">Privacyverklaring</h1>
        <div className="mt-10 prose-koning space-y-6 text-koning-muted leading-relaxed">
          <p className="text-sm">Laatst bijgewerkt: juni 2026</p>

          <H>Wie is verantwoordelijk voor uw gegevens?</H>
          <p>
            Koning Aannemersbedrijf, gevestigd te Maassluis (KvK {company.kvk}, BTW {company.btw}), is de
            verwerkingsverantwoordelijke voor de persoonsgegevens die via deze website worden verzameld.
            Voor vragen over uw privacy kunt u contact opnemen via{" "}
            <a href={company.emailHref} className="text-koning-gold hover:underline">{company.email}</a> of{" "}
            <a href={company.phoneHref} className="text-koning-gold hover:underline">{company.phone}</a>.
          </p>

          <H>Welke gegevens verzamelen wij en waarvoor?</H>
          <p>Wij verwerken uitsluitend gegevens die u zelf via onze formulieren deelt, of die (na uw toestemming) via cookies worden verzameld:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong className="text-white">Offerte- en contactaanvragen</strong> — naam, e-mailadres, telefoonnummer, woonplaats/postcode en projectinformatie. Doel: het behandelen van uw aanvraag en het opstellen van een offerte. Grondslag: uitvoering van (pre)contractuele maatregelen op uw verzoek.</li>
            <li><strong className="text-white">Checklist-download en subsidieberekening (PDF)</strong> — naam, e-mailadres en telefoonnummer. Doel: het toesturen van het gevraagde document en persoonlijke opvolging. Grondslag: uitvoering op uw verzoek; nieuwsbrief-updates alleen met uw aparte toestemming.</li>
            <li><strong className="text-white">Cookies en statistieken</strong> — zie de cookieparagraaf hieronder. Grondslag: uw toestemming via de cookiebanner (functionele cookies: gerechtvaardigd belang).</li>
          </ul>

          <H>Hoe lang bewaren wij uw gegevens?</H>
          <ul className="list-disc pl-6 space-y-2">
            <li>Offerte- en contactaanvragen die niet tot een opdracht leiden: maximaal 24 maanden na het laatste contact, daarna verwijderd.</li>
            <li>Gegevens die onderdeel worden van een overeenkomst (offertes, facturen): 7 jaar, conform de wettelijke fiscale bewaarplicht.</li>
            <li>Nieuwsbrief-inschrijvingen: tot u zich afmeldt.</li>
            <li>Analytics-gegevens (Google Analytics 4): 14 maanden.</li>
          </ul>

          <H>Met wie delen wij gegevens (ontvangers/verwerkers)?</H>
          <p>Wij verkopen uw gegevens nooit. Wij gebruiken de volgende diensten die (delen van) uw gegevens verwerken:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong className="text-white">Emergent</strong> — hosting van de website en de beveiligde database waarin aanvragen worden opgeslagen.</li>
            <li><strong className="text-white">Resend</strong> — verzending van e-mails (bevestigingen, offerte-opvolging, documenten).</li>
            <li><strong className="text-white">Cloudinary</strong> — hosting en optimalisatie van projectfoto&apos;s op de website (geen klantgegevens).</li>
            <li><strong className="text-white">Google</strong> — Google Analytics 4 (statistieken, alleen na toestemming) en het live ophalen van onze Google-reviews.</li>
            <li><strong className="text-white">Meta</strong> — Meta Pixel voor advertentiemeting, alleen na uw expliciete marketing-toestemming.</li>
            <li><strong className="text-white">Microsoft Clarity</strong> — geanonimiseerde gebruiksstatistieken, alleen na toestemming.</li>
            <li><strong className="text-white">CallMeBot</strong> — interne WhatsApp-notificatie aan ons team bij een nieuwe aanvraag (naam en contactgegevens), zodat wij u snel kunnen terugbellen.</li>
          </ul>
          <p>
            Enkele van deze partijen (Google, Meta, Microsoft, Resend, Cloudinary) zijn gevestigd in de Verenigde Staten.
            Doorgifte vindt plaats op basis van het EU-VS Data Privacy Framework en/of EU-standaardcontractbepalingen (SCC&apos;s).
          </p>

          <H>Uw rechten</H>
          <p>
            U heeft het recht op inzage, rectificatie, verwijdering en beperking van de verwerking van uw persoonsgegevens,
            het recht op dataportabiliteit en het recht om <strong className="text-white">bezwaar</strong> te maken tegen de verwerking.
            Gegeven toestemming (bijvoorbeeld voor cookies of de nieuwsbrief) kunt u op elk moment intrekken.
            Stuur uw verzoek aan{" "}
            <a href={company.emailHref} className="text-koning-gold hover:underline">{company.email}</a> — wij reageren binnen 30 dagen.
            Bent u niet tevreden over de afhandeling, dan kunt u een klacht indienen bij de Autoriteit Persoonsgegevens
            (autoriteitpersoonsgegevens.nl).
          </p>

          <H>Cookies en tracking</H>
          <p>Wij gebruiken drie categorieën cookies. Analytics- en marketingcookies worden pas geplaatst nadat u daarvoor toestemming heeft gegeven via de cookiebanner:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong className="text-white">Functioneel (altijd actief)</strong> — onmisbaar voor de werking van de website, formulier­invoer en het onthouden van uw cookie-voorkeur.</li>
            <li><strong className="text-white">Analytics (optioneel)</strong> — Google Analytics 4 en Microsoft Clarity verzamelen geanonimiseerde gegevens over uw bezoek (pagina&apos;s, duur, herkomst) om de site te verbeteren. IP-adressen worden geanonimiseerd. Bewaartermijn: 14 maanden.</li>
            <li><strong className="text-white">Marketing (optioneel)</strong> — Meta (Facebook) Pixel en Google Ads conversie-tracking meten welke advertenties leiden tot een offerte-aanvraag of download. Deze cookies worden uitsluitend geplaatst na uw expliciete toestemming.</li>
          </ul>
          <p>U kunt uw voorkeuren op elk moment wijzigen via de link <em>&ldquo;Cookie-voorkeuren&rdquo;</em> onderaan de pagina.</p>

          <H>UTM-tracking</H>
          <p>Wanneer u via een advertentielink op onze website komt, slaan wij tijdelijk een UTM-tag op in uw browser (sessionStorage). Deze tag wordt aan een eventuele offerte-aanvraag of PDF-download gekoppeld, zodat wij weten welke campagne welke aanvraag oplevert. De tag verdwijnt automatisch zodra u uw browser sluit.</p>

          <H>Beveiliging</H>
          <p>Uw gegevens worden versleuteld verzonden (HTTPS/TLS) en opgeslagen in een beveiligde database met toegangs­beperking. Alleen geautoriseerde medewerkers hebben toegang tot aanvraaggegevens.</p>
        </div>
      </div>
    </section>
  </>
);

export const VoorwaardenPage = () => (
  <>
    <Meta title="Algemene Voorwaarden | Koning Aannemersbedrijf" />
    <section className="pt-24 pb-20 md:pt-32 md:pb-28" data-testid="voorwaarden-page">
      <div className="max-w-[900px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="eyebrow mb-5">Algemene voorwaarden</div>
        <h1 className="font-display text-4xl md:text-6xl text-white tracking-tight">Algemene Voorwaarden</h1>
        <p className="mt-10 text-koning-muted leading-relaxed">
          Op al onze aanbiedingen, offertes en overeenkomsten zijn de algemene voorwaarden van Koning Aannemersbedrijf van toepassing. Deze worden op verzoek kosteloos toegezonden en zijn beschikbaar bij ondertekening van de offerte.
        </p>
      </div>
    </section>
  </>
);
